<template>
  <v-card>
    <v-toolbar color="primary" flat dark prominent>
      <v-toolbar-title>{{ dealer.name }}</v-toolbar-title>
      <v-spacer/>
      <v-card-subtitle class="white--text">Sales and Service Summary &nbsp; <strong>{{ fStartDate }} - {{ fEndDate }}</strong></v-card-subtitle>
      <v-btn
        color="primary"
        fab
        right
        x-small
        elevation="0"
        v-on:click.prevent="$emit('close-report')"
      >
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col cols="6">
          <v-toolbar :color="salesToolbarColor" flat dark dense>
            <v-toolbar-title>Sales</v-toolbar-title>
          </v-toolbar>
          <v-row no-gutters class="mt-4">
            <stat-card
              label="Total"
              :color="salesColor"
              :value="formattedData['sale_ro_count'] || 0"
              icon="mdi-car-back"
            ></stat-card>
          </v-row>
          <v-row no-gutters class="mt-4">
            <vs-stacked-bar-chart
              name="Sales RO Breakdown"
              :vs-data="salesCountVsData"
            >
            </vs-stacked-bar-chart>
          </v-row>
           <v-row no-gutters class="mt-4">
            <stat-card
              label="Profit"
              :color="salesColor"
              :value="formattedData['sale_ro_profit'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Revenue"
              :color="salesColor"
              :value="formattedData['sale_ro_revenue'] || 0 | toCurrency"
              icon="mdi-car-back"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Avg Profit"
              :color="salesColor"
              :value="formattedData['sale_ro_avg_profit'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Avg Revenue"
              :color="salesColor"
              :value="formattedData['sale_ro_avg_revenue'] || 0 | toCurrency"
              icon="mdi-car-back"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
          </v-row>
          <v-row no-gutters class="mt-4">
            <vs-stacked-bar-chart
              name="Sales Revenue Breakdown"
              :vs-data="salesRevenueVsData"
            >
            </vs-stacked-bar-chart>
          </v-row>
       </v-col>
        <v-col cols="6">
          <v-toolbar :color="servicesToolbarColor" flat dark dense>
            <v-toolbar-title>Services</v-toolbar-title>
          </v-toolbar>
          <v-row no-gutters class="mt-4">
            <stat-card
              label="Total"
              :color="servicesColor"
              :value="formattedData['service_ro_count'] || 0"
              icon="mdi-car-battery"
            ></stat-card>
          </v-row>
          <v-row no-gutters class="mt-4">
            <vs-stacked-bar-chart
              name="Service RO Breakdown"
              :vs-data="serviceCountVsData"
            >
            </vs-stacked-bar-chart>
          </v-row>
          <v-row no-gutters class="mt-4">
            <stat-card
              label="Profit"
              :color="servicesColor"
              :value="formattedData['service_ro_profit'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Revenue"
              :color="servicesColor"
              :value="formattedData['service_ro_revenue'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Avg Profit"
              :color="servicesColor"
              :value="formattedData['service_ro_avg_profit'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
            <stat-card
              label="Avg Revenue"
              :color="servicesColor"
              :value="formattedData['service_ro_avg_revenue'] || 0 | toCurrency"
              icon="mdi-account"
              :card-dimensions-override="dollarTextShrink"
            ></stat-card>
          </v-row>
          <v-row no-gutters class="mt-4">
            <vs-stacked-bar-chart
              name="Service Revenue Breakdown"
              :vs-data="serviceRevenueVsData"
            >
            </vs-stacked-bar-chart>
          </v-row>

       </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import StatCard from '@/components/ui/StatCard'
import VsStackedBarChart from '@/components/charts/VsStackedBarChart'
import { ReportsApi } from '@/services'
import { downloadViaLinkCreated } from '@/utils'
import { DateTime } from 'luxon'

export default {
  name: 'dealer-ro-report',
  components: {
    [StatCard.name]: StatCard,
    [VsStackedBarChart.name]: VsStackedBarChart
  },
  props: {
    // when set to true, indicate report should be executed now
    runReport: {
      required: true,
      type: Boolean
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    outputFormat: {
      type: String,
      required: false,
      default: 'html'
    }
  },
  data: function () {
    return {
      salesToolbarColor: 'teal',
      salesColor: 'teal--text',
      servicesToolbarColor: 'cyan',
      servicesColor: 'cyan--text',
      reportStatsData: {},
      dollarTextShrink: {
        default: {
          // width: 200,
          valueClass: 'headline ml-2 mb-8'
        }
      }
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    fStartDate () {
      return this.dateVerboseMDY(this.startDate)
    },
    fEndDate () {
      return this.dateVerboseMDY(this.endDate)
    },
    totalSalesCount () { return this.reportStatsData.total_ro_count },
    formattedData () {
      return Object.keys(this.reportStatsData).reduce((accum, field) => {
        if (field.includes('profit') || field.includes('revenue')) {
          accum[field] = Number(this.reportStatsData[field])
        } else {
          accum[field] = this.reportStatsData[field]
        }
        return accum
      }, {})
    },
    salesCountVsData () {
      return [
        [
          { label: 'Conquest', data: this.formattedData.sale_conquest_customer_ro_count || 0 },
          { label: 'Repeat', data: this.formattedData.sale_repeat_customer_ro_count || 0 }
        ],
        [
          { label: 'New', data: this.formattedData.sale_new_vehicle_ro_count || 0 },
          { label: 'Used', data: this.formattedData.sale_used_vehicle_ro_count || 0 }
        ],
        [
          { label: 'Finance', data: this.formattedData.sale_financed_vehicle_ro_count || 0 },
          { label: 'Lease', data: this.formattedData.sale_leased_vehicle_ro_count || 0 }
        ],
        [
          { label: 'Individual', data: this.formattedData.sale_individual_customer_ro_count || 0 },
          { label: 'Business', data: this.formattedData.sale_business_customer_ro_count || 0 }
        ]
      ]
    },
    salesRevenueVsData () {
      return [
        [
          { label: 'Conquest', data: this.formattedData.sale_conquest_customer_ro_revenue || 0 },
          { label: 'Repeat', data: this.formattedData.sale_repeat_customer_ro_revenue || 0 }
        ],
        [
          { label: 'New', data: this.formattedData.sale_new_vehicle_ro_revenue || 0 },
          { label: 'Used', data: this.formattedData.sale_used_vehicle_ro_revenue || 0 }
        ],
        [
          { label: 'Finance', data: this.formattedData.sale_financed_vehicle_ro_revenue || 0 },
          { label: 'Lease', data: this.formattedData.sale_leased_vehicle_ro_revenue || 0 }
        ],
        [
          { label: 'Individual', data: this.formattedData.sale_individual_customer_ro_revenue || 0 },
          { label: 'Business', data: this.formattedData.sale_business_customer_ro_revenue || 0 }
        ]
      ]
    },

    serviceCountVsData () {
      return [
        [
          { label: 'Conquest', data: this.formattedData.service_conquest_customer_ro_count || 0 },
          { label: 'Repeat', data: this.formattedData.service_repeat_customer_ro_count || 0 }
        ],
        [
          { label: 'Individual', data: this.formattedData.service_individual_customer_ro_count || 0 },
          { label: 'Business', data: this.formattedData.service_business_customer_ro_count || 0 }
        ]
      ]
    },
    serviceRevenueVsData () {
      return [
        [
          { label: 'Conquest', data: this.formattedData.service_conquest_customer_ro_revenue || 0 },
          { label: 'Repeat', data: this.formattedData.service_repeat_customer_ro_revenue || 0 }
        ],
        [
          { label: 'Individual', data: this.formattedData.service_individual_customer_ro_revenue || 0 },
          { label: 'Business', data: this.formattedData.service_business_customer_ro_revenue || 0 }
        ]
      ]
    }

  },
  watch: {
    runReport (newVal, oldVal) {
      this.$logger.trace('Processing runReport change')
      if (newVal) {
        this.loadReportData()
      }
    }
  },
  methods: {
    shouldDownload () {
      return ['excel', 'csv'].includes(this.outputFormat)
    },
    dateVerboseMDY (date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED)
    },
    async loadReportData () {
      this.$logger.trace('Loading Report Data')
      try {
        const rptParams = {
          name: 'RoReport',
          dealer_id: this.dealer.id,
          start_date: this.startDate,
          end_date: this.endDate,
          include_backing_data: this.shouldDownload(),
          output_format: this.outputFormat
        }
        const rptData = await ReportsApi.generateReport(rptParams)
        if (this.shouldDownload()) {
          downloadViaLinkCreated(rptData.data.csv_url)
        } else {
          this.reportStatsData = rptData.data[0] || {}
          this.$logger.trace(this.reportStatsData)
        }

        this.$emit('report-complete', { status: 'ok' })
      } catch (error) {
        this.$logger.error('Error Fetching report', error.message)
        this.$emit('report-complete', { status: 'error', message: error.message })
      }
    },
    getStat (statName) {
      return this.reportStatsData[statName] || 0
    }
  }

}
</script>
